import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { PurchaseOrderDocType, PurchaseOrderItem, ProductDocType, LancarPOSDatabase } from '@lancar-pos/db-schema';

export type PurchaseOrder = PurchaseOrderDocType;

export function usePurchaseOrderStore() {
  const db = useDatabase();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.purchase_orders
      .find({
        selector: { _deleted: false },
        sort: [{ createdAt: 'desc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => PurchaseOrder }>) => {
        setPurchaseOrders(docs.map((doc: { toMutableJSON: () => PurchaseOrder }) => doc.toMutableJSON() as PurchaseOrder));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.purchase_orders]);

  const addPurchaseOrder = useCallback(
    async (order: Omit<PurchaseOrder, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt' | '_deleted'>) => {
      const now = new Date().toISOString();
      const orderNumber = await generateOrderNumber(db);
      
      await db.purchase_orders.insert({
        ...order,
        id: crypto.randomUUID(),
        orderNumber,
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      });
    },
    [db.purchase_orders]
  );

  const updatePurchaseOrder = useCallback(
    async (id: string, updates: Partial<Omit<PurchaseOrder, 'id' | 'createdAt' | 'orderNumber'>>) => {
      const doc = await db.purchase_orders.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.purchase_orders]
  );

  const deletePurchaseOrder = useCallback(
    async (id: string) => {
      const doc = await db.purchase_orders.findOne(id).exec();
      if (doc) {
        await doc.patch({
          _deleted: true,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.purchase_orders]
  );

  const getPurchaseOrderById = useCallback(
    (id: string): PurchaseOrder | undefined => {
      return purchaseOrders.find((po) => po.id === id);
    },
    [purchaseOrders]
  );

  const filterByStatus = useCallback(
    (status: PurchaseOrder['status'] | 'all'): PurchaseOrder[] => {
      if (status === 'all') return purchaseOrders;
      return purchaseOrders.filter((po) => po.status === status);
    },
    [purchaseOrders]
  );

  const filterBySupplier = useCallback(
    (supplierId: string): PurchaseOrder[] => {
      return purchaseOrders.filter((po) => po.supplierId === supplierId);
    },
    [purchaseOrders]
  );

  const updateStatus = useCallback(
    async (id: string, status: PurchaseOrder['status']) => {
      const doc = await db.purchase_orders.findOne(id).exec();
      if (doc) {
        const updates: Partial<PurchaseOrder> = {
          status,
          updatedAt: new Date().toISOString(),
        };
        
        if (status === 'received') {
          updates.receivedDate = new Date().toISOString();
        }
        
        await doc.patch(updates);
      }
    },
    [db.purchase_orders]
  );

  const receiveGoods = useCallback(
    async (id: string, items: PurchaseOrderItem[]) => {
      const doc = await db.purchase_orders.findOne(id).exec();
      if (!doc) return;
      
      for (const item of items) {
        const product = await db.products.findOne(item.productId).exec();
        if (product) {
          const currentProduct = product.toMutableJSON() as ProductDocType;
          await product.patch({
            stock: currentProduct.stock + item.receivedQuantity,
            updatedAt: new Date().toISOString(),
          });
        }
      }

      await doc.patch({
        items,
        status: 'received',
        receivedDate: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
    [db.purchase_orders, db.products]
  );

  return {
    purchaseOrders,
    isLoading,
    addPurchaseOrder,
    updatePurchaseOrder,
    deletePurchaseOrder,
    getPurchaseOrderById,
    filterByStatus,
    filterBySupplier,
    updateStatus,
    receiveGoods,
  };
}

async function generateOrderNumber(db: LancarPOSDatabase): Promise<string> {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const prefix = `PO-${year}${month}${day}`;
  
  const allOrders = await db.purchase_orders.find().exec();
  const todayOrders = allOrders.filter((o: { orderNumber: string }) => o.orderNumber.startsWith(prefix));
  const nextNumber = todayOrders.length + 1;
  
  return `${prefix}-${String(nextNumber).padStart(3, '0')}`;
}

export type PurchaseOrderStore = ReturnType<typeof usePurchaseOrderStore>;
